import { useReactiveVar } from '@apollo/client'
import CircularProgress from '@material-ui/core/CircularProgress'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import clsx from 'clsx'
import { useSwitcherNavigation } from 'components/WorkspaceEditor/features/keyOperations/hooks/useSwitcherNavigation'
import {
    createSwitcherClickHandler,
    handleExpandCollapseKeys,
} from 'components/WorkspaceEditor/features/keyOperations/utils/switcherOperations.utils'
import {
    useContentItemFetcher,
    usePermissionChecks,
    useTreeHandlers,
} from 'components/WorkspaceEditor/hooks'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useContext } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'
import type { DataNode, NodeType } from '../../types/types'

/** Props for rendering switcher icon of <TreeView /> component */

export interface SwitcherIconProps {
    /** The node type */
    type: NodeType
    /* A flag whether the node is loading */
    isLoading: boolean
    /* A flag whether the node is expanded */
    isExpanded: boolean
    /* A flag whether the expand/collapse functionality is disabled */
    isDisabled?: boolean
    'data-testid'?: string
    node: DataNode
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

/**
 * Renders the switcher icon property of <TreeView /> component
 * @param {SwitcherIconProps} props - Props for rendering switcher icon
 * @returns {ReactElement} Node switcher icon
 */
export const SwitcherIcon = memo<SwitcherIconProps>(
    ({
        type,
        isLoading,
        isExpanded,
        isDisabled = false,
        'data-testid': dataTestId = 'switcher-icon',
        node,
        treeRef,
    }) => {
        const classes = useTreeViewStyles()

        const {
            treeData,
            setTreeData,
            expandedKeys,
            articlesLoading,
        } = useShallowSelector((s) => ({
            treeData: s.treeData,
            setTreeData: s.actions.setTreeData,
            expandedKeys: s.expandedKeys,
            articlesLoading: s.articlesLoading,
        }))

        const {
            selectedElementNode: selectedProjectElementNode,
        } = useReactiveVar(workspaceSelectedElement)
        const permission = usePermissionChecks(
            treeData,
            selectedProjectElementNode
        )

        const { projectId } = useContext(ProjectPageContext)
        const {
            fetchContentItemView: fetchProductFamily,
        } = useContentItemFetcher({
            projectId: projectId!,
            treeData,
            setTreeData,
            permission,
        })

        const {
            handleMouseHover,
            handleMouseLeave,
            handleExpandCollapseArticle,
        } = useTreeHandlers(fetchProductFamily)

        // Navigation hook for arrow key handling
        const { handleArrowNavigation, handlePreExpansion } = useSwitcherNavigation({
            node,
            treeData,
            expandedKeys: expandedKeys.map(String),
            articlesLoading,
            treeRef,
        })

        // Click handlers for expand/collapse spans
        const expandClickHandler: React.MouseEventHandler<HTMLSpanElement> = isDisabled 
            ? (e) => { e.preventDefault(); e.stopPropagation(); }
            : createSwitcherClickHandler(
                true, // shouldExpand
                node,
                expandedKeys.map(String),
                handleExpandCollapseArticle
            )
        const collapseClickHandler: React.MouseEventHandler<HTMLSpanElement> = isDisabled
            ? (e) => { e.preventDefault(); e.stopPropagation(); }
            : createSwitcherClickHandler(
                false, // shouldExpand (collapse)
                node,
                expandedKeys.map(String),
                handleExpandCollapseArticle
            )

        const handleKeyDown = useCallback(
            async (e: React.KeyboardEvent<HTMLElement>) => {
                // If disabled, prevent any keyboard actions
                if (isDisabled) {
                    e.preventDefault()
                    e.stopPropagation()
                    return
                }
                
                // Save focus state before expansion (if applicable)
                handlePreExpansion(e, node, isExpanded)

                // Handle expand/collapse keys using utility function
                const handled = handleExpandCollapseKeys(
                    e,
                    node,
                    isExpanded,
                    expandedKeys.map(String),
                    handleExpandCollapseArticle
                )

                // If expand/collapse wasn't handled, try arrow navigation
                if (!handled) {
                    await handleArrowNavigation(e)
                }
            },
            [
                node,
                isExpanded,
                isDisabled,
                expandedKeys,
                handleExpandCollapseArticle,
                handleArrowNavigation,
                handlePreExpansion,
            ]
        )

        // only Articles can have switcherIcon (expand/collapse)
        if (type !== NODE_TYPES.Article) {
            return (
                <div
                    onMouseOver={() => handleMouseHover(node)}
                    onMouseLeave={handleMouseLeave}
                    className={`${classes.hiddenContainer} leaf`}
                />
            )
        }

        if (isLoading) {
            return (
                <CircularProgress
                    data-testid={dataTestId}
                    className={classes.switcherLoadingIcon}
                    size={16}
                />
            )
        }

        return (
            <div
                onMouseOver={() => handleMouseHover(node)}
                onMouseLeave={handleMouseLeave}
                className={clsx('switcherIcon', classes.switcherIconContainer)}
            >
                {isExpanded ? (
                    <span
                        onClick={collapseClickHandler}
                        onKeyDown={handleKeyDown}
                        data-testid={dataTestId}
                        className={isDisabled ? classes.disabledSwitcherIcon : classes.switcherIcon}
                        role="button"
                        aria-label="Collapse Article"
                        aria-disabled={isDisabled}
                        tabIndex={isDisabled ? -1 : 0}
                    >
                        <ExpandMoreIcon />
                    </span>
                ) : (
                    <span
                        onClick={expandClickHandler}
                        onKeyDown={handleKeyDown}
                        data-testid={dataTestId}
                        className={isDisabled ? classes.disabledSwitcherIcon : classes.switcherIconCollapsed}
                        role="button"
                        aria-label="Expand Article"
                        aria-disabled={isDisabled}
                        tabIndex={isDisabled ? -1 : 0}
                    >
                        <ChevronRightIcon />
                    </span>
                )}
            </div>
        )
    }
)
